import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
interface Flag {
  name: string;
  enabled: boolean;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

// ── Spinner ──────────────────────────────────────────────────────────
const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Toggle Switch ────────────────────────────────────────────────────
interface ToggleSwitchProps {
  enabled: boolean;
  toggling: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ enabled, toggling, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={toggling || disabled}
    className={`
      relative inline-flex h-6 w-11 shrink-0 items-center rounded-full
      transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:cursor-not-allowed disabled:opacity-50
      ${enabled
        ? 'bg-emerald-500 focus:ring-emerald-500/40'
        : 'bg-gray-300 focus:ring-gray-300/40'
      }
    `}
    aria-checked={enabled}
    role="switch"
  >
    <span
      className={`
        inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow
        transform transition-transform duration-200
        ${enabled ? 'translate-x-5' : 'translate-x-0.5'}
      `}
    >
      {toggling && <Spinner />}
    </span>
  </button>
);

// ── Toast Container ──────────────────────────────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}
  </div>
);

// ── Skeleton Card ────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="space-y-2 flex-1">
        <div className="h-3 bg-gray-200 rounded w-2/3" />
        <div className="h-5 bg-gray-100 rounded w-12" />
      </div>
      <div className="h-6 w-11 bg-gray-200 rounded-full ml-4" />
    </div>
  </div>
);

// ── Main Flags Component ─────────────────────────────────────────────
const Flags: React.FC = () => {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingFlag, setTogglingFlag] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toastIdRef = useRef(0);

  // ── Toast helpers ────────────────────────────────────────────────
  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Fetch flags ──────────────────────────────────────────────────
  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get<{ flags: Flag[]; total: number }>('/admin/flags');
      setFlags(res.data.flags);
    } catch {
      addToast('Failed to load feature flags.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // ── Toggle flag ──────────────────────────────────────────────────
  const toggleFlag = async (flagName: string, currentValue: boolean) => {
    if (togglingFlag) return;

    // Optimistic update
    setTogglingFlag(flagName);
    setFlags((prev) =>
      prev.map((f) => (f.name === flagName ? { ...f, enabled: !currentValue } : f))
    );

    try {
      await adminApi.post(`/admin/flags/${flagName}`);
      const newState = !currentValue ? 'enabled' : 'disabled';
      addToast(`${flagName} ${newState}`, 'success');
    } catch {
      // Revert on error
      setFlags((prev) =>
        prev.map((f) => (f.name === flagName ? { ...f, enabled: currentValue } : f))
      );
      addToast(`Failed to toggle ${flagName}`, 'error');
    } finally {
      setTogglingFlag(null);
    }
  };

  // ── Filtered flags ───────────────────────────────────────────────
  const filteredFlags = flags.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase().trim())
  );

  const enabledCount = flags.filter((f) => f.enabled).length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">⚑ Feature Flags</h1>
        <p className="text-sm text-gray-500 mt-1">
          Toggle features on/off. Changes apply instantly but reset on server restart.
        </p>
      </div>

      {/* Warning banner */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
        <span className="text-lg shrink-0 mt-0.5">⚠️</span>
        <p className="text-sm font-medium">
          Changes are in-memory only. Server restart will reset all flags to their default values.
        </p>
      </div>

      {/* Search + counter */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search flags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white
              text-sm text-gray-800 placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
              shadow-sm transition-all duration-200"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {!loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 shrink-0">
            <span className="font-semibold text-emerald-600">{enabledCount}</span>
            <span>on /</span>
            <span className="font-semibold text-gray-800">{flags.length}</span>
            <span>total</span>
            {search && (
              <span className="text-gray-400">
                · <span className="font-semibold text-gray-600">{filteredFlags.length}</span> shown
              </span>
            )}
          </div>
        )}
      </div>

      {/* Flag grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)
        ) : filteredFlags.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-16 gap-3">
            <span className="text-4xl">🔍</span>
            <p className="text-gray-400 font-medium">No flags match "{search}"</p>
          </div>
        ) : (
          filteredFlags.map((flag) => (
            <div
              key={flag.name}
              className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3.5
                flex items-center justify-between gap-3
                hover:border-gray-200 hover:shadow transition-all duration-150"
            >
              {/* Left: name + badge */}
              <div className="min-w-0 space-y-1.5">
                <p className="text-sm font-mono text-gray-700 truncate" title={flag.name}>
                  {flag.name}
                </p>
                <span className={`
                  inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full
                  ${flag.enabled
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-gray-100 text-gray-500'
                  }
                `}>
                  {flag.enabled && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  )}
                  {flag.enabled ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Right: toggle */}
              <div title={flag.name === 'admin_panel' && flag.enabled ? "Cannot disable admin_panel: would lock out all admins" : undefined}>
                <ToggleSwitch
                  enabled={flag.enabled}
                  toggling={togglingFlag === flag.name}
                  onClick={() => toggleFlag(flag.name, flag.enabled)}
                  disabled={flag.name === 'admin_panel' && flag.enabled}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Toasts */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default Flags;
